import { createFileRoute } from '@tanstack/react-router'
import * as React from "react";
import { API_ADMIN_BASE_URL } from "@/lib/api-client";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious,
} from "@/components/ui/pagination";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Search } from "lucide-react";

interface SubItem {
  id: string;
  plan: string;
  status: string;
  monthlyCredits: number;
  creditsUsed: number;
  maxCanvases: number;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  user: { id: string; email: string; name: string };
}

interface SubStats {
  byPlan: Record<string, number>;
  byStatus: Record<string, number>;
  mrr: number;
  churnThisMonth: number;
}

const PLAN_COLORS: Record<string, string> = { FREE: "", PRO: "text-blue-600 border-blue-600/30", ENTERPRISE: "text-purple-600 border-purple-600/30" };
const STATUS_COLORS: Record<string, string> = { ACTIVE: "text-green-600", CANCELED: "text-red-600", PAST_DUE: "text-yellow-600", UNPAID: "text-red-500", INCOMPLETE: "text-gray-500" };

const fmtDate = (d: string | null) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "-";

function SubscriptionsPage() {
    const [subs, setSubs] = React.useState<SubItem[]>([]);
  const [stats, setStats] = React.useState<SubStats | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [total, setTotal] = React.useState(0);
  const [search, setSearch] = React.useState("");
  const [planFilter, setPlanFilter] = React.useState("");
  const [page, setPage] = React.useState(1);
  const limit = 20;

  const fetchSubs = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", limit.toString());
      params.set("offset", ((page - 1) * limit).toString());
      if (search) params.set("search", search);
      if (planFilter) params.set("plan", planFilter);
      const res = await fetch(`${API_ADMIN_BASE_URL}/api/admin/subscriptions?${params}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      const json = await res.json();
      setSubs(json.data.subscriptions);
      setTotal(json.data.total);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [search, planFilter, page]);

  React.useEffect(() => { fetchSubs(); }, [fetchSubs]);
  React.useEffect(() => {
    fetch(`${API_ADMIN_BASE_URL}/api/admin/subscriptions/stats`, { credentials: "include" })
      .then((r) => r.json()).then((j) => setStats(j.data)).catch(() => {});
  }, []);
  React.useEffect(() => { document.title = "Subscriptions - Admin"; }, []);
  React.useEffect(() => { setPage(1); }, [search, planFilter]);

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="bg-background h-full overflow-auto">
      <header>
        <div className="border-border flex h-12 shrink-0 items-center gap-2 border-b px-4 md:h-16 md:px-6">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem><BreadcrumbLink href="/dashboard">Dashboard</BreadcrumbLink></BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem><BreadcrumbPage>Subscriptions</BreadcrumbPage></BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        <div className="border-border border-b px-4 py-4 md:px-6 md:py-6">
          <h1 className="text-2xl font-bold tracking-tight md:text-3xl">Subscriptions</h1>
          <p className="text-muted-foreground text-sm">{total.toLocaleString()} subscriptions</p>
          {stats && (
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-6">
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">Free</span><span className="text-lg font-semibold">{(stats.byPlan.FREE || 0).toLocaleString()}</span></div>
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">Pro</span><span className="text-lg font-semibold text-blue-600">{(stats.byPlan.PRO || 0).toLocaleString()}</span></div>
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">Enterprise</span><span className="text-lg font-semibold text-purple-600">{(stats.byPlan.ENTERPRISE || 0).toLocaleString()}</span></div>
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">MRR</span><span className="text-lg font-semibold">${stats.mrr.toLocaleString()}</span></div>
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">Active</span><span className="text-lg font-semibold text-green-600">{(stats.byStatus.ACTIVE || 0).toLocaleString()}</span></div>
              <div className="flex flex-col"><span className="text-muted-foreground text-xs">Churn (Month)</span><span className="text-lg font-semibold text-red-600">{stats.churnThisMonth}</span></div>
            </div>
          )}
        </div>
      </header>
      <main className="flex-1 space-y-6 px-4 py-6 md:px-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full md:w-[300px]">
            <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
            <Input placeholder="Search by email..." className="pl-8" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="flex gap-2">
            <Badge variant={planFilter === "" ? "default" : "outline"} className="cursor-pointer" onClick={() => setPlanFilter("")}>All</Badge>
            {["FREE", "PRO", "ENTERPRISE"].map((p) => (
              <Badge key={p} variant={planFilter === p ? "default" : "outline"} className={`cursor-pointer ${planFilter !== p ? PLAN_COLORS[p] : ""}`} onClick={() => setPlanFilter(planFilter === p ? "" : p)}>{p}</Badge>
            ))}
          </div>
        </div>
        <div className="relative overflow-hidden rounded-lg border">
          <div className="overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Credits</TableHead>
                  <TableHead className="text-center">Max Canvases</TableHead>
                  <TableHead>Period End</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
                ) : subs.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No subscriptions found</TableCell></TableRow>
                ) : subs.map((s) => (
                  <TableRow key={s.id} className="hover:bg-muted/50">
                    <TableCell><div><p className="font-medium">{s.user.name}</p><p className="text-muted-foreground text-xs">{s.user.email}</p></div></TableCell>
                    <TableCell><Badge variant="outline" className={`text-xs ${PLAN_COLORS[s.plan] || ""}`}>{s.plan}</Badge></TableCell>
                    <TableCell><Badge variant="outline" className={`text-xs ${STATUS_COLORS[s.status] || ""}`}>{s.status}</Badge></TableCell>
                    <TableCell><span className="text-sm">{s.creditsUsed}/{s.monthlyCredits}</span></TableCell>
                    <TableCell className="text-center">{s.maxCanvases}</TableCell>
                    <TableCell><span className="text-muted-foreground text-xs">{fmtDate(s.currentPeriodEnd)}</span></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
        {totalPages > 1 && (
          <Pagination>
            <PaginationContent>
              <PaginationItem><PaginationPrevious onClick={() => setPage(Math.max(1, page - 1))} className={page === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"} /></PaginationItem>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pn: number; if (totalPages <= 5) pn = i + 1; else if (page <= 3) pn = i + 1; else if (page >= totalPages - 2) pn = totalPages - 4 + i; else pn = page - 2 + i;
                return (<PaginationItem key={pn}><PaginationLink onClick={() => setPage(pn)} isActive={page === pn} className="cursor-pointer">{pn}</PaginationLink></PaginationItem>);
              })}
              <PaginationItem><PaginationNext onClick={() => setPage(Math.min(totalPages, page + 1))} className={page === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"} /></PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </main>
    </div>
  );
}

export const Route = createFileRoute('/dashboard/subscriptions')({
  component: SubscriptionsPage,
})
